import { MainInner } from "@/styles/global";
import {
  DetailsBig,
  Paragraph,
  ParagraphCss,
  PrimaryButtonCss,
  h4,
  h6,
} from "@/styles/typography";
import styled from "styled-components";

const Inner = styled(MainInner)`
  align-items: center;
`;

const FormWrapper = styled.div`
  background: ${({ theme }) => theme.colors.coreDark};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 4px;
  width: 100%;

  @media (min-width: 650px) {
    width: 596px;
    padding: 40px;
  }
`;

const H4 = styled(h4)``;

const H6 = styled(h6)``;

const Button = styled.button`
  display: flex;
  padding: 12px 20px 10px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  font-family: "Frutiger Bold";
  border: none;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0.225px;
  border: 2px solid ${({ theme }) => theme.colors.textHeading};
  color: ${({ theme }) => theme.colors.textHeading};
  background: transparent;
  cursor: pointer;

  &.active {
    background: ${({ theme }) => theme.colors.darkPurple};
    color: ${({ theme }) => theme.colors.coreLight};
    border: 2px solid transparent;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const DetailsLarge = styled(DetailsBig)`
  &::before {
    content: "*";
    color: #e93cac;
  }

  &.not-required {
    &::before {
      content: "";
    }
  }
`;

const SmallLabel = styled.label`
  ${ParagraphCss}
  font-size: 15px;

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.textBody};
    font-weight: 700;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RadioButton = styled.input`
  height: 18px;
  width: 18px;
  margin-right: 8px;
  margin-bottom: -3px;
  cursor: pointer;

  &.custom-radio {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid ${({ theme }) => theme.colors.textBody};
    background-color: transparent;
    border-radius: 50%;
  }
  &.custom-radio:checked {
    background-color: ${({ theme }) => theme.colors.primaryPurple};
    border: 3px solid ${({ theme }) => theme.colors.coreDark};
    box-shadow: 0px 0px 0px 1px ${({ theme }) => theme.colors.textBody};
  }
  &.error {
    background: #ffbdbd;
  }
`;

const Input = styled.input`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.coreLight};
  border: none;
  display: flex;
  width: 100%;
  padding: 16px;
  align-items: flex-start;
  gap: 8px;
  font-size: 15px;
  font-family: "Minion Pro";
  outline: none;

  &::placeholder {
    font-size: 15px;
    font-family: "Minion Pro";
    color: ${({ theme }) => theme.colors.textBody};
  }
  &:placeholder-shown {
    &.error {
      background: #ffbdbd;
    }
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.textHeading};
  font-family: "Frutiger Bold";
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0.225px;

  &::after {
    content: "*";
    color: #e93cac;
  }

  &.not-required {
    &::after {
      content: "";
    }
  }
`;

const TextArea = styled.textarea`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.coreLight};
  border: none;
  display: flex;
  width: 100%;
  padding: 16px;
  align-items: flex-start;
  gap: 8px;
  height: 200px;
  resize: none;
  font-size: 15px;
  font-family: "Minion Pro";
  outline: none;

  &::placeholder {
    font-size: 15px;
    font-family: "Minion Pro";
    color: ${({ theme }) => theme.colors.textBody};
  }

  &:placeholder-shown {
    &.error {
      background: #ffbdbd;
    }
  }
`;

const UploadButton = styled.button`
  background: transparent;
  font-family: "Frutiger Bold";
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  letter-spacing: 0.36px;
  display: flex;
  padding: 16px 28px 14px 28px;
  align-items: center;
  gap: 10px;
  border-radius: 48px;
  width: fit-content;
  border: 2px solid ${({ theme }) => theme.colors.textHeading};

  svg {
    margin-bottom: 4px;
  }
`;

const CheckboxWrapper = styled.div`
  display: inline-block;
  position: relative;
  cursor: pointer;

  & input[type="checkbox"]:checked + svg {
    transform: translate(-50%, -50%) scale(1);
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.1s ease;
    pointer-events: none;
  }
`;

const Checkbox = styled.input`
  appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-content: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.colors.textHeading};
  border-radius: 2px;
  cursor: pointer;

  &:before {
    content: "";
    width: 20px;
    height: 20px;
    transition: transform 0.1s ease;
    transform: scale(0);
    background-color: transparent;
  }

  &:not(:checked) {
    &.error {
      background: #ffbdbd;
    }
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubmitButton = styled.button`
  ${PrimaryButtonCss}
`;

const FormError = styled.div``;

const Error = styled(Paragraph)`
  color: ${({ theme }) => theme.colors.textBody};
  font-size: 14px;
`;

const S = {
  Inner,
  FormWrapper,
  H4,
  H6,
  Button,
  Wrapper,
  SmallLabel,
  DetailsLarge,
  Form,
  RadioButton,
  Input,
  FlexWrapper,
  Label,
  TextArea,
  UploadButton,
  Checkbox,
  SubmitButton,
  FormError,
  CheckboxWrapper,
  CheckboxContainer,
  Error,
};

export default S;
